import React from "react";
import styled from "styled-components";
import { ProjectContainer, ProjectTitle, ProjectInfo } from "./styles";
import { useLanguage } from "../../contexts/LanguageContext";

const ProjectDescription = styled.div`
  margin-bottom: 30px;
  line-height: 24px;
`;

const ProjectWrapper = styled.div`
  margin-bottom: 40px;

  h4 {
    font-size: 20px;
    color: #333;
    margin: 0 0 20px 0;
    font-weight: 600;
  }

  h5 {
    font-size: 16px;
    color: #333;
    margin: 20px 0 10px 0;
    font-weight: 600;
  }

  img {
    width: 100%;
    max-width: 600px;
    height: auto;
    margin-bottom: 20px;
    border: 1px solid #eee;
  }

  ul {
    li {
      margin-bottom: 15px;
      line-height: 24px;

      a {
        color: #f34452;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .btn {
    background: #f34452;
    color: #fff;
    padding: 12px 30px;
    text-decoration: none;
    text-transform: uppercase;
    font-weight: 600;
    border-radius: 3px;
    transition: all 0.3s;
    display: inline-block;
    border: none;
    cursor: pointer;

    &:hover {
      background: #d63946;
    }
  }

  ul.list-inline {
    list-style: none;
    padding: 0;
    display: flex;
    gap: 20px;

    li {
      b {
        color: #333;
      }
    }
  }
`;

interface ProjectCscwProps {}

const ProjectCscw: React.FC<ProjectCscwProps> = () => {
  const { t } = useLanguage();

  return (
    <ProjectContainer>
      <ProjectTitle>
        <h2>{t("cscw.title")}</h2>
        <p className="tags">{t("cscw.role")}</p>
      </ProjectTitle>

      <ProjectDescription>{t("cscw.description")}</ProjectDescription>

      <ProjectInfo>
        <p>
          <b>{t("cscw.roleLabel")}</b> {t("cscw.myRole")}
        </p>
        <p>
          <b>{t("cscw.skillsLabel")}</b> {t("cscw.skillsUtilized")}
        </p>
      </ProjectInfo>

      <ProjectWrapper>
        <h4>{t("cscw.researchExperience")}</h4>
        <ul>
          <li>{t("cscw.experience1")}</li>
          <li>{t("cscw.experience2")}</li>
          <li>{t("cscw.experience3")}</li>
          <li>{t("cscw.experience4")}</li>
          <li>
            {t("cscw.experience5")}{" "}
            <a
              href="http://www.brainstorming.sinaapp.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              (www.brainstorming.sinaapp.com/)
            </a>
          </li>
        </ul>
      </ProjectWrapper>

      <ProjectWrapper>
        <h4>{t("cscw.publications")}</h4>
        <ul>
          <li>
            {t("cscw.publication1")} (
            <a
              href="http://onlinelibrary.wiley.com/doi/10.1111/bjet.12382/full"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.seeDetails")}
            </a>{" "}
            ||{" "}
            <a
              href="documents/BJET.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.viewPdf")}
            </a>
            )
          </li>
          <li>
            {t("cscw.publication2")} (
            <a
              href="http://ieeexplore.ieee.org/xpls/abs_all.jsp?arnumber=7069699"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.seeDetails")}
            </a>{" "}
            ||{" "}
            <a
              href="documents/can_we_ solve.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.viewPdf")}
            </a>
            )
          </li>
          <li>
            {t("cscw.publication3")} (
            <a
              href="http://aisel.aisnet.org/cgi/viewcontent.cgi?article=1376&context=pacis2014"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.seeDetails")}
            </a>{" "}
            ||{" "}
            <a
              href="documents/aisel.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.viewPdf")}
            </a>
            )
          </li>
          <li>
            {t("cscw.publication4")} (
            <a
              href="http://ieeexplore.ieee.org/xpls/abs_all.jsp?arnumber=6758660&tag=1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.seeDetails")}
            </a>{" "}
            ||{" "}
            <a
              href="documents/easy.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.viewPdf")}
            </a>
            )
          </li>
          <li>
            {t("cscw.publication5")} (
            <a
              href="http://citeseerx.ist.psu.edu/viewdoc/download?doi=10.1.1.299.8811&rep=rep1&type=pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.seeDetails")}
            </a>{" "}
            ||{" "}
            <a
              href="documents/online.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("cscw.viewPdf")}
            </a>
            )
          </li>
        </ul>
      </ProjectWrapper>

      <ProjectWrapper>
        <h4>{t("cscw.designBrainstorming")}</h4>
        <div>
          <h5>{t("cscw.firstSketch")}</h5>
          <img loading="lazy" decoding="async"
            className="img-responsive"
            src="/images/projects/cscw1-min.webp"
            alt="discussion-screen"
          />
          <h5>{t("cscw.finalDesign")}</h5>
          <img loading="lazy" decoding="async"
            className="img-responsive"
            src="/images/projects/cscw2-min.webp"
            alt="discussion-screen"
          />
        </div>
        <h4>{t("cscw.responsiveDevelopment")}</h4>
        <p>{t("cscw.techStack")}</p>
        <div>
          <h5>{t("cscw.launchedApp")}</h5>
          <img loading="lazy" decoding="async"
            className="img-responsive"
            src="/images/projects/cscw3-min.webp"
            alt="discussion-screen"
          />
        </div>
      </ProjectWrapper>

      <ProjectWrapper>
        <ul className="list-inline">
          <li>
            <b>{t("cscw.dateLabel")}</b> {t("cscw.dateRange")}
          </li>
        </ul>
      </ProjectWrapper>
    </ProjectContainer>
  );
};

export default ProjectCscw;
